import {
  addCalendarYearsMonths,
  addCivilDays,
  buildHolidayMap,
  isBusinessDay,
  toCivilDate,
} from '../day-helpers.js';
import { buildObservedHolidays } from '../metadata-helpers.js';
import type { ResolveProceduralDateInput, ResolveProceduralDateResult } from '../types.js';

/**
 * US Federal calendar days per FRCP 6(a): exclude the trigger day, then count
 * every calendar day (weekends and federal holidays included). If the final
 * day lands on a Saturday, Sunday, or federal holiday, the deadline rolls
 * forward to the next business day (FRCP 6(a)(1)(C)). Honours `years` /
 * `months` / `days` on the interval.
 */
export function resolveUsFederalCalendar(
  input: ResolveProceduralDateInput,
): ResolveProceduralDateResult {
  const start = toCivilDate(input.startDate);
  const { years = 0, months = 0, days = 0 } = input.interval;
  const holidayMap = buildHolidayMap(input.holidays);

  const afterYearsMonths = addCalendarYearsMonths(start, years, months);
  const naiveResult = addCivilDays(afterYearsMonths, days);
  const forward = naiveResult.getTime() >= start.getTime();

  const step = forward ? 1 : -1;
  let resultDate = naiveResult;
  while (!isBusinessDay(resultDate, holidayMap)) {
    resultDate = addCivilDays(resultDate, step);
  }
  const endDateRolledForward = resultDate.getTime() !== naiveResult.getTime();

  const countedDates: Date[] = [];
  if (naiveResult.getTime() !== start.getTime()) {
    let d = addCivilDays(start, step);
    while (forward ? d.getTime() <= naiveResult.getTime() : d.getTime() >= naiveResult.getTime()) {
      countedDates.push(d);
      d = addCivilDays(d, step);
    }
  }

  const windowStart = resultDate.getTime() < start.getTime() ? resultDate : start;
  const windowEnd = resultDate.getTime() < start.getTime() ? start : resultDate;

  return {
    resultDate,
    jurisdiction: 'us-federal',
    dayCount: 'calendar',
    direction:
      resultDate.getTime() === start.getTime()
        ? 'same-day'
        : resultDate.getTime() > start.getTime()
          ? 'after'
          : 'before',
    startDateExcluded: true,
    endDateRolledForward,
    weekendsCount: true,
    publicHolidaysCount: true,
    countedDates,
    skippedDates: [],
    observedHolidays: buildObservedHolidays(holidayMap, windowStart, windowEnd),
    windowStart,
    windowEnd,
    warnings: [],
  };
}
